import React, { useRef, useEffect, useState } from "react";

interface SignaturePadProps {
  onSignatureCapture: (signatureData: string) => void;
  onClear: () => void;
}

interface DrawPoint {
  x: number;
  y: number;
}

const SignaturePad: React.FC<SignaturePadProps> = ({
  onSignatureCapture,
  onClear,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureMode, setSignatureMode] = useState<"draw" | "type">("draw");
  const [typedSignature, setTypedSignature] = useState("");
  const [penSize, setPenSize] = useState(4);
  const [isErasing, setIsErasing] = useState(false);
  const [fontStyle, setFontStyle] = useState("italic 48px cursive");
  const [hasCanvasContent, setHasCanvasContent] = useState(false);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (context && canvas) {
      context.fillStyle = "white";
      context.fillRect(0, 0, 450, 200);
    }
  };

  const switchSignatureMode = (mode: "draw" | "type") => {
    if (mode === signatureMode) return;

    clearCanvas();
    setIsDrawing(false);
    setIsErasing(false);

    if (mode === "draw") {
      setTypedSignature("");
    } else {
      setHasCanvasContent(false);
    }

    setSignatureMode(mode);
    onClear();
  };

  // Initialize canvas when mounted or mode changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const context = canvas.getContext("2d");
      if (context) {
        // Reset transform and clear
        context.setTransform(1, 0, 0, 1, 0, 0);
        const dpr = window.devicePixelRatio || 1;
        canvas.width = 450 * dpr;
        canvas.height = 200 * dpr;
        context.scale(dpr, dpr);

        context.fillStyle = "white";
        context.fillRect(0, 0, 450, 200);
        context.strokeStyle = "#000000";
        context.lineWidth = penSize;
        context.lineCap = "round";
        context.lineJoin = "round";

        // If we just switched to type mode and have text, draw it immediately
        if (signatureMode === "type" && typedSignature) {
          drawTypedSignature(context);
        }
      }
    }
  }, [signatureMode]);

  // Helper to draw typed signature on canvas
  const drawTypedSignature = (ctx: CanvasRenderingContext2D) => {
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, 450, 200);
    ctx.fillStyle = "#000";
    ctx.font = fontStyle;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(typedSignature, 450 / 2, 200 / 2);
  };

  // Update canvas in real-time as user types
  useEffect(() => {
    if (signatureMode === "type") {
      const canvas = canvasRef.current;
      const context = canvas?.getContext("2d");
      if (context) {
        drawTypedSignature(context);
      }
    }
  }, [typedSignature, fontStyle, signatureMode]);

  // Update pen color/size
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const context = canvas.getContext("2d");
      if (context) {
        context.strokeStyle = isErasing ? "white" : "#000000";
        context.lineWidth = penSize;
      }
    }
  }, [penSize, isErasing]);

  const getCoordinates = (
    e:
      | React.MouseEvent<HTMLCanvasElement>
      | React.TouchEvent<HTMLCanvasElement>,
  ): DrawPoint | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const scaleX = 450 / rect.width;
    const scaleY = 200 / rect.height;

    let clientX, clientY;
    if ("touches" in e) {
      const touch = e.touches[0];
      clientX = touch.clientX;
      clientY = touch.clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (
    e:
      | React.MouseEvent<HTMLCanvasElement>
      | React.TouchEvent<HTMLCanvasElement>,
  ) => {
    if (signatureMode !== "draw") return;
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    const point = getCoordinates(e);

    if (context && point) {
      context.beginPath();
      context.moveTo(point.x, point.y);
    }
  };

  const draw = (
    e:
      | React.MouseEvent<HTMLCanvasElement>
      | React.TouchEvent<HTMLCanvasElement>,
  ) => {
    if (!isDrawing || signatureMode !== "draw") return;
    if (e.cancelable) e.preventDefault();

    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    const point = getCoordinates(e);

    if (context && point) {
      context.lineTo(point.x, point.y);
      context.stroke();
      setHasCanvasContent(true);
    }
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    clearCanvas();

    if (signatureMode === "draw") {
      setHasCanvasContent(false);
    } else {
      setTypedSignature("");
    }
    onClear();
  };

  const captureSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (signatureMode === "draw" && !hasCanvasContent) {
      alert("Please draw your signature before confirming");
      return;
    }
    if (signatureMode === "type" && !typedSignature.trim()) {
      alert("Please type your signature before confirming");
      return;
    }

    const signatureData = canvas.toDataURL("image/png");
    onSignatureCapture(signatureData);
  };

  const hasValidSignature =
    signatureMode === "draw" ? hasCanvasContent : typedSignature.trim() !== "";

  return (
    <div className="space-y-4">
      {/* Mode Selector */}
      <div className="flex gap-2 border-b border-gray-100 pb-5">
        <button
          onClick={() => switchSignatureMode("draw")}
          className={`flex-1 py-2.5 rounded-xl font-semibold transition-all duration-200 text-sm sm:text-base ${
            signatureMode === "draw"
              ? "bg-blue-50 text-blue-700 shadow-sm border border-blue-200"
              : "bg-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-700 border border-transparent"
          }`}
        >
          ✏️ Draw
        </button>
        <button
          onClick={() => switchSignatureMode("type")}
          className={`flex-1 py-2.5 rounded-xl font-semibold transition-all duration-200 text-sm sm:text-base ${
            signatureMode === "type"
              ? "bg-blue-50 text-blue-700 shadow-sm border border-blue-200"
              : "bg-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-700 border border-transparent"
          }`}
        >
          ✍️ Type
        </button>
      </div>

      {/* Persistent Canvas Container */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          {signatureMode === "draw"
            ? "Draw your signature"
            : "Signature Preview"}
        </label>

        {signatureMode === "draw" && (
          <div className="flex items-center gap-4">
            <label className="block text-sm font-medium text-gray-600">
              Pen Size: {penSize}px
            </label>
            <input
              type="range"
              min="1"
              max="8"
              value={penSize}
              onChange={(e) => setPenSize(parseInt(e.target.value))}
              className="flex-1"
            />
          </div>
        )}

        <div className="border-2 border-dashed border-gray-300 rounded-2xl bg-white p-2 shadow-sm hover:border-blue-400 transition-colors group">
          <canvas
            ref={canvasRef}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            className={`bg-white rounded-xl w-full ${signatureMode === "draw" ? "cursor-crosshair" : "cursor-default"} touch-none`}
            style={{ maxWidth: "100%", height: "auto" }}
            width={450}
            height={200}
          />
        </div>

        {signatureMode === "draw" && (
          <>
            <p className="text-xs text-gray-500">
              Click and drag to draw • Use pen controls to customize
            </p>
            <button
              onClick={() => setIsErasing(!isErasing)}
              className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                isErasing
                  ? "bg-red-500 text-white"
                  : "bg-gray-200 text-gray-700"
              }`}
            >
              {isErasing ? "🧹 Erasing" : "🧹 Eraser"}
            </button>
          </>
        )}
      </div>

      {/* Type Mode Controls */}
      {signatureMode === "type" && (
        <div className="space-y-3 animate-in fade-in duration-300">
          <label className="block text-sm font-medium text-gray-700">
            Type your name
          </label>
          <input
            type="text"
            value={typedSignature}
            onChange={(e) => setTypedSignature(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && captureSignature()}
            placeholder="Enter your name"
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-400 transition-all text-lg font-medium text-gray-800 placeholder-gray-400"
          />

          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-600">
              Font Style
            </label>
            <div className="flex gap-2 flex-wrap">
              {[
                { label: "Cursive", value: "italic 48px cursive" },
                {
                  label: "Script",
                  value: "48px 'Lucida Handwriting', cursive",
                },
                { label: "Bold", value: "bold 48px Arial" },
                { label: "Normal", value: "48px Arial" },
              ].map((style) => (
                <button
                  key={style.value}
                  onClick={() => setFontStyle(style.value)}
                  className={`px-3 py-1 rounded text-xs font-medium ${
                    fontStyle === style.value
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-700"
                  }`}
                >
                  {style.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3 pt-6 mt-4 border-t border-gray-100">
        <button
          onClick={clearSignature}
          className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200 rounded-xl font-semibold transition-colors shadow-sm text-sm sm:text-base"
        >
          Clear
        </button>
        <button
          onClick={captureSignature}
          disabled={!hasValidSignature}
          className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl font-semibold disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed shadow-md focus:ring-4 focus:ring-blue-200 transition-all text-sm sm:text-base space-x-2"
        >
          <span>✓</span>
          <span>Confirm Signature</span>
        </button>
      </div>
    </div>
  );
};

export default SignaturePad;
